import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tv, Plus, Copy, Check, X, RefreshCw, Trash2, Pencil, Video, GripVertical, Globe, ImageIcon, Building2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import toast from 'react-hot-toast';
import { mediaPlayersApi, camerasApi, videosApi, branchesApi } from '../services/api';
import type { MediaPlayer, MediaPlayerWithKey, PlaylistItem } from '../types';

const SERVER_URL = import.meta.env.VITE_API_BASE_URL || 'https://humanvu-api.mndev.co.za/api';

export default function MediaPlayers() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<MediaPlayer | null>(null);
  const [newPlayer, setNewPlayer] = useState<MediaPlayerWithKey | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCameraId, setEditCameraId] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addVideoId, setAddVideoId] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addItemType, setAddItemType] = useState<'video' | 'web_url' | 'image'>('video');
  const [playlistItems, setPlaylistItems] = useState<PlaylistItem[]>([]);
  const [playlistDirty, setPlaylistDirty] = useState(false);
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [addBranchId, setAddBranchId] = useState<string>('');
  const [editBranchId, setEditBranchId] = useState<string>('');
  const queryClient = useQueryClient();

  // Fetch media players
  const { data: players, isLoading } = useQuery({
    queryKey: ['media-players'],
    queryFn: () => mediaPlayersApi.list(),
    refetchInterval: 10000,
  });

  // Fetch cameras for linking
  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: () => camerasApi.list(),
  });

  // Fetch videos for playlist
  const { data: videos } = useQuery({
    queryKey: ['videos'],
    queryFn: () => videosApi.list(),
  });

  // Fetch branches
  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: () => branchesApi.list(),
  });

  // Filter and group players by branch
  const filteredPlayers = useMemo(() => {
    if (!players) return [];
    if (!filterBranchId) return players;
    if (filterBranchId === 'unassigned') return players.filter((p) => !p.branch_id);
    return players.filter((p) => p.branch_id === filterBranchId);
  }, [players, filterBranchId]);

  const groupedPlayers = useMemo(() => {
    if (!branches || !filteredPlayers.length) return { branches: [], unassigned: filteredPlayers };

    const branchGroups = branches
      .map((branch) => ({
        branch,
        players: filteredPlayers.filter((p) => p.branch_id === branch.id),
      }))
      .filter((group) => group.players.length > 0);

    const unassigned = filteredPlayers.filter((p) => !p.branch_id);

    return { branches: branchGroups, unassigned };
  }, [branches, filteredPlayers]);

  // Fetch playlist for selected player
  const { data: playlist } = useQuery({
    queryKey: ['media-player-playlist', selectedPlayer?.id],
    queryFn: () => mediaPlayersApi.getPlaylist(selectedPlayer!.id),
    enabled: !!selectedPlayer,
  });

  // Keep selectedPlayer in sync with query data
  useEffect(() => {
    if (selectedPlayer && players) {
      const updated = players.find((p) => p.id === selectedPlayer.id);
      if (updated) {
        setSelectedPlayer(updated);
      }
    }
  }, [players]);

  // Sync playlist local state when fetched
  useEffect(() => {
    if (playlist) {
      setPlaylistItems(playlist);
      setPlaylistDirty(false);
    }
  }, [playlist]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: ({ name, cameraId, branchId }: { name: string; cameraId?: string; branchId?: string }) =>
      mediaPlayersApi.create(name, cameraId, branchId),
    onSuccess: (data) => {
      setNewPlayer(data);
      queryClient.invalidateQueries({ queryKey: ['media-players'] });
      toast.success('Media player created');
    },
    onError: () => {
      toast.error('Failed to create media player');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; camera_id?: string | null; branch_id?: string | null } }) =>
      mediaPlayersApi.update(id, data),
    onSuccess: (updatedPlayer) => {
      setSelectedPlayer(updatedPlayer);
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['media-players'] });
      toast.success('Media player updated');
    },
    onError: () => {
      toast.error('Failed to update media player');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => mediaPlayersApi.delete(id),
    onSuccess: () => {
      closeDetailsModal();
      queryClient.invalidateQueries({ queryKey: ['media-players'] });
      toast.success('Media player deleted');
    },
    onError: () => {
      toast.error('Failed to delete media player');
    },
  });

  // Regenerate code mutation
  const regenerateCodeMutation = useMutation({
    mutationFn: (id: string) => mediaPlayersApi.regenerateCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-players'] });
      toast.success('Registration code regenerated');
    },
    onError: () => {
      toast.error('Failed to regenerate code');
    },
  });

  // Update playlist mutation
  const updatePlaylistMutation = useMutation({
    mutationFn: ({ id, items }: { id: string; items: { video_id?: string | null; item_type?: string; url?: string | null; duration_seconds?: number | null; position: number }[] }) =>
      mediaPlayersApi.updatePlaylist(id, items),
    onSuccess: (updatedPlaylist) => {
      setPlaylistItems(updatedPlaylist);
      setPlaylistDirty(false);
      queryClient.invalidateQueries({ queryKey: ['media-player-playlist', selectedPlayer?.id] });
      toast.success('Playlist updated');
    },
    onError: () => {
      toast.error('Failed to update playlist');
    },
  });

  const getCameraName = (cameraId: string | null): string => {
    if (!cameraId || !cameras) return 'No camera linked';
    const camera = cameras.find((c) => c.id === cameraId);
    return camera?.name || 'Unknown camera';
  };

  const getVideoName = (videoId: string): string => {
    if (!videos) return videoId;
    const video = videos.find((v) => v.id === videoId);
    return video?.filename || videoId;
  };

  const handleAddPlayer = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const cameraId = formData.get('camera_id') as string;
    createMutation.mutate({
      name: formData.get('name') as string,
      cameraId: cameraId || undefined,
      branchId: addBranchId || undefined,
    });
  };

  const copyApiKey = async () => {
    if (newPlayer) {
      await navigator.clipboard.writeText(newPlayer.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const copyRegistrationCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const getTvUrl = (code: string) =>
    `${window.location.origin}/tv?server=${encodeURIComponent(SERVER_URL)}&code=${code}`;

  const copyTvUrl = async (code: string) => {
    await navigator.clipboard.writeText(getTvUrl(code));
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setNewPlayer(null);
    setCopiedKey(false);
    setCopiedCode(false);
    setCopiedUrl(false);
    setAddBranchId('');
  };

  const closeDetailsModal = () => {
    setSelectedPlayer(null);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setPlaylistItems([]);
    setPlaylistDirty(false);
    setAddVideoId('');
    setCopiedCode(false);
  };

  const startEditing = () => {
    if (selectedPlayer) {
      setEditName(selectedPlayer.name);
      setEditCameraId(selectedPlayer.camera_id || '');
      setEditBranchId(selectedPlayer.branch_id || '');
      setIsEditing(true);
    }
  };

  const handleSaveEdit = () => {
    if (selectedPlayer) {
      updateMutation.mutate({
        id: selectedPlayer.id,
        data: {
          name: editName,
          camera_id: editCameraId || null,
          branch_id: editBranchId || null,
        },
      });
    }
  };

  const handleAddVideo = () => {
    if (!addVideoId) return;
    const newItems: PlaylistItem[] = [
      ...playlistItems,
      {
        id: `temp-${Date.now()}`,
        video_id: addVideoId,
        item_type: 'video',
        url: null,
        duration_seconds: null,
        position: playlistItems.length,
        video: videos?.find((v) => v.id === addVideoId) || null,
      },
    ];
    setPlaylistItems(newItems);
    setPlaylistDirty(true);
    setAddVideoId('');
  };

  const handleAddUrl = () => {
    if (!addUrl) return;
    const newItems: PlaylistItem[] = [
      ...playlistItems,
      {
        id: `temp-${Date.now()}`,
        video_id: null,
        item_type: addItemType,
        url: addUrl,
        duration_seconds: null,
        position: playlistItems.length,
        video: null,
      },
    ];
    setPlaylistItems(newItems);
    setPlaylistDirty(true);
    setAddUrl('');
  };

  const handleRemovePlaylistItem = (index: number) => {
    const newItems = playlistItems
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, position: i }));
    setPlaylistItems(newItems);
    setPlaylistDirty(true);
  };

  const handleSavePlaylist = () => {
    if (!selectedPlayer) return;
    const items = playlistItems.map((item, index) => ({
      video_id: item.video_id,
      item_type: item.item_type || 'video',
      url: item.url,
      duration_seconds: item.duration_seconds,
      position: index,
    }));
    updatePlaylistMutation.mutate({ id: selectedPlayer.id, items });
  };

  const cameraOptions = [
    { value: '', label: 'No camera (optional)' },
    ...(cameras?.map((c) => ({ value: c.id, label: c.name })) || []),
  ];

  const availableVideos = videos?.filter(
    (v) => !playlistItems.some((item) => item.video_id === v.id)
  ) || [];

  const videoOptions = [
    { value: '', label: 'Select a video...' },
    ...availableVideos.map((v) => ({ value: v.id, label: v.filename })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Media Players</h1>
          <p className="text-gray-500 dark:text-slate-400">Manage your Android TV media player devices</p>
        </div>
        <div className="flex items-center space-x-3">
          {branches && branches.length > 0 && (
            <select
              value={filterBranchId}
              onChange={(e) => setFilterBranchId(e.target.value)}
              className="text-sm border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="unassigned">Unassigned</option>
            </select>
          )}
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Media Player
          </Button>
        </div>
      </div>

      {/* Media Players Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading media players...</div>
      ) : filteredPlayers.length > 0 ? (
        <div className="space-y-6">
          {/* Branch groups */}
          {groupedPlayers.branches.map(({ branch, players: branchPlayers }) => (
            <div key={branch.id}>
              <div className="flex items-center space-x-2 mb-3">
                <Building2 className="w-4 h-4 text-primary-500 dark:text-primary-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">{branch.name}</h3>
                <span className="text-sm text-gray-500 dark:text-slate-400">({branchPlayers.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {branchPlayers.map((player) => (
                  <Card
                    key={player.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${player.is_online ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
                          <Tv className={`w-5 h-5 ${player.is_online ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'}`} />
                        </div>
                        <div className="ml-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white">{player.name}</h3>
                          <div className="flex items-center text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                            <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${player.is_online ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`} />
                            {player.is_online ? 'Online' : 'Offline'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t dark:border-slate-700 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-slate-400">Camera</span>
                        <span className="text-gray-900 dark:text-white truncate ml-2">{getCameraName(player.camera_id)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-slate-400">Last seen</span>
                        <span className="text-gray-900 dark:text-white">{player.last_seen_at ? new Date(player.last_seen_at).toLocaleString() : 'Never'}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {/* Unassigned players */}
          {groupedPlayers.unassigned.length > 0 && (
            <div>
              {groupedPlayers.branches.length > 0 && (
                <div className="flex items-center space-x-2 mb-3">
                  <h3 className="font-semibold text-gray-500 dark:text-slate-400">Unassigned</h3>
                  <span className="text-sm text-gray-400 dark:text-slate-500">({groupedPlayers.unassigned.length})</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedPlayers.unassigned.map((player) => (
                  <Card
                    key={player.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${player.is_online ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
                          <Tv className={`w-5 h-5 ${player.is_online ? 'text-green-600' : 'text-gray-400 dark:text-slate-500'}`} />
                        </div>
                        <div className="ml-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white">{player.name}</h3>
                          <div className="flex items-center text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                            <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${player.is_online ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`} />
                            {player.is_online ? 'Online' : 'Offline'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t dark:border-slate-700 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-slate-400">Camera</span>
                        <span className="text-gray-900 dark:text-white truncate ml-2">{getCameraName(player.camera_id)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-slate-400">Last seen</span>
                        <span className="text-gray-900 dark:text-white">{player.last_seen_at ? new Date(player.last_seen_at).toLocaleString() : 'Never'}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card className="text-center py-12">
          <Tv className="w-12 h-12 mx-auto text-gray-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No media players yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">
            Add your first media player to start displaying content on Android TV
          </p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Media Player
          </Button>
        </Card>
      )}

      {/* Add Media Player Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:bg-slate-800 border dark:border-slate-700">
            {!newPlayer ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Add New Media Player
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </button>
                </div>
                <form onSubmit={handleAddPlayer} className="space-y-4">
                  <Input
                    label="Player Name"
                    name="name"
                    placeholder="e.g., Store Front Display"
                    required
                  />
                  <Select
                    label="Link to Camera (optional)"
                    name="camera_id"
                    options={cameraOptions}
                  />
                  {branches && branches.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Branch (optional)</label>
                      <select
                        value={addBranchId}
                        onChange={(e) => setAddBranchId(e.target.value)}
                        className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-slate-200"
                      >
                        <option value="">No branch</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button type="submit" isLoading={createMutation.isPending}>
                      Create Player
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Media Player Created!
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                  Save these credentials - the API key won't be shown again.
                </p>

                {/* Registration Code */}
                {newPlayer.registration_code && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                      Registration Code
                    </label>
                    <div className="flex items-center justify-center">
                      <code className="text-3xl font-mono font-bold text-blue-900 dark:text-blue-100 tracking-widest">
                        {newPlayer.registration_code}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyRegistrationCode(newPlayer.registration_code!)}
                        className="ml-3"
                      >
                        {copiedCode ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-3 text-center">
                      Add this URL to your CMS playlist:
                    </p>
                    <div className="flex items-center bg-blue-100 dark:bg-blue-900/30 rounded p-2 mt-2">
                      <code className="flex-1 text-xs font-mono text-blue-800 dark:text-blue-200 break-all">
                        {getTvUrl(newPlayer.registration_code!)}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyTvUrl(newPlayer.registration_code!)}
                        className="ml-2 shrink-0"
                      >
                        {copiedUrl ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full mt-3"
                      onClick={() => window.open(getTvUrl(newPlayer.registration_code!), '_blank')}
                    >
                      <Tv className="w-4 h-4 mr-2" />
                      Launch Web TV Player
                    </Button>
                  </div>
                )}

                {/* API Key */}
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                    API Key
                  </label>
                  <div className="flex items-center">
                    <code className="flex-1 text-sm font-mono break-all text-gray-900 dark:text-white">
                      {newPlayer.api_key}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyApiKey}
                      className="ml-2"
                    >
                      {copiedKey ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-amber-900/20 border border-yellow-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800 dark:text-amber-300">
                    Save the API key now - it won't be shown again. You can use the
                    registration code or the API key to connect the Android TV app.
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button onClick={closeModal}>Done</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Media Player Details Modal */}
      {selectedPlayer && !showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto dark:bg-slate-800 border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedPlayer.name}
                </h2>
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    selectedPlayer.is_online
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                  }`}
                >
                  {selectedPlayer.is_online ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                {!isEditing && (
                  <>
                    <button
                      onClick={startEditing}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                      title="Edit player"
                    >
                      <Pencil className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete player"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </>
                )}
                <button
                  onClick={closeDetailsModal}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Name and Camera */}
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Name
                    </label>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Player name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Linked Camera
                    </label>
                    <Select
                      options={cameraOptions}
                      value={editCameraId}
                      onChange={(e) => setEditCameraId(e.target.value)}
                    />
                  </div>
                  {branches && branches.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                        Branch
                      </label>
                      <select
                        value={editBranchId}
                        onChange={(e) => setEditBranchId(e.target.value)}
                        className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-slate-200"
                      >
                        <option value="">No branch</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      isLoading={updateMutation.isPending}
                      className="flex-1"
                    >
                      Save
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Name
                    </label>
                    <p className="text-gray-900 dark:text-white font-medium">{selectedPlayer.name}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                      Linked Camera
                    </label>
                    <p className="text-gray-900 dark:text-white">{getCameraName(selectedPlayer.camera_id)}</p>
                  </div>

                  {selectedPlayer.branch_id && branches && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                        Branch
                      </label>
                      <div className="flex items-center text-gray-900 dark:text-white">
                        <Building2 className="w-4 h-4 text-primary-500 dark:text-primary-400 mr-1.5" />
                        {branches.find((b) => b.id === selectedPlayer.branch_id)?.name || 'Unknown'}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Status
                </label>
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                    selectedPlayer.is_online
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                  }`}
                >
                  {selectedPlayer.is_online ? 'Online' : 'Offline'}
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Last Seen
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedPlayer.last_seen_at
                    ? new Date(selectedPlayer.last_seen_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>

              {/* Registration Code */}
              <div className="border-t dark:border-slate-700 pt-4 mt-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                  Registration Code
                </label>
                {selectedPlayer.registration_code ? (
                  <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <code className="text-lg font-mono font-bold text-gray-900 dark:text-white tracking-widest">
                        {selectedPlayer.registration_code}
                      </code>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyRegistrationCode(selectedPlayer.registration_code!)}
                        >
                          {copiedCode ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => regenerateCodeMutation.mutate(selectedPlayer.id)}
                          isLoading={regenerateCodeMutation.isPending}
                          title="Regenerate code"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {selectedPlayer.registration_code_expires_at && (
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        Expires: {new Date(selectedPlayer.registration_code_expires_at).toLocaleString()}
                      </p>
                    )}
                    <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded p-2 mt-2">
                      <code className="flex-1 text-xs font-mono text-gray-700 dark:text-slate-200 break-all">
                        {getTvUrl(selectedPlayer.registration_code!)}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyTvUrl(selectedPlayer.registration_code!)}
                        className="ml-2 shrink-0"
                      >
                        {copiedUrl ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => window.open(getTvUrl(selectedPlayer.registration_code!), '_blank')}
                    >
                      <Tv className="w-4 h-4 mr-2" />
                      Launch Web TV
                    </Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">No active registration code</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => regenerateCodeMutation.mutate(selectedPlayer.id)}
                      isLoading={regenerateCodeMutation.isPending}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Generate Code
                    </Button>
                  </div>
                )}
              </div>

              {/* Playlist Section */}
              <div className="border-t dark:border-slate-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Video className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
                    <label className="text-sm font-medium text-gray-900 dark:text-white">
                      Playlist
                    </label>
                  </div>
                  {playlistDirty && (
                    <Button
                      size="sm"
                      onClick={handleSavePlaylist}
                      isLoading={updatePlaylistMutation.isPending}
                    >
                      Save Playlist
                    </Button>
                  )}
                </div>

                {/* Playlist Items */}
                {playlistItems.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {playlistItems.map((item, index) => (
                      <div
                        key={item.id}
                        className="flex items-center bg-gray-50 dark:bg-slate-900 rounded-lg p-2"
                      >
                        <GripVertical className="w-4 h-4 text-gray-300 dark:text-slate-600 mr-2 flex-shrink-0" />
                        <span className="text-xs text-gray-400 dark:text-slate-500 mr-2 w-5 text-center">
                          {index + 1}
                        </span>
                        {item.item_type === 'web_url' ? (
                          <Globe className="w-3.5 h-3.5 text-blue-400 mr-2 flex-shrink-0" />
                        ) : item.item_type === 'image' ? (
                          <ImageIcon className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" />
                        ) : (
                          <Video className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 mr-2 flex-shrink-0" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-white flex-1 truncate">
                          {item.item_type === 'video'
                            ? (item.video?.filename || getVideoName(item.video_id || ''))
                            : item.url || 'URL'}
                        </span>
                        <button
                          onClick={() => handleRemovePlaylistItem(index)}
                          className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-2 flex-shrink-0"
                          title="Remove from playlist"
                        >
                          <X className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-3 mb-3">
                    No items in playlist
                  </p>
                )}

                {/* Add to Playlist */}
                <div className="space-y-2">
                  <div className="flex gap-1">
                    {(['video', 'web_url', 'image'] as const).map((type) => (
                      <button
                        key={type}
                        onClick={() => setAddItemType(type)}
                        className={`px-2 py-1 text-xs rounded ${
                          addItemType === type
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {type === 'video' ? 'Video' : type === 'web_url' ? 'Web URL' : 'Image'}
                      </button>
                    ))}
                  </div>

                  {addItemType === 'video' ? (
                    availableVideos.length > 0 && (
                      <div className="flex space-x-2">
                        <div className="flex-1">
                          <Select
                            options={videoOptions}
                            value={addVideoId}
                            onChange={(e) => setAddVideoId(e.target.value)}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddVideo}
                          disabled={!addVideoId}
                          className="flex-shrink-0 self-end"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )
                  ) : (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={addUrl}
                        onChange={(e) => setAddUrl(e.target.value)}
                        placeholder={addItemType === 'web_url' ? 'https://example.com' : 'https://example.com/image.jpg'}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 dark:text-slate-200 dark:placeholder-slate-500 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddUrl}
                        disabled={!addUrl}
                        className="flex-shrink-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={closeDetailsModal}>Close</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {selectedPlayer && showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:bg-slate-800 border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Delete Media Player
              </h2>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This will permanently delete the media player{' '}
                <strong>{selectedPlayer.name}</strong> and its playlist.
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(selectedPlayer.id)}
                isLoading={deleteMutation.isPending}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Player
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
